import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectProjectSettings } from '../../../core/state/bookingProjectSettings/selectors';
import { Actions as BookingActions } from '../../../core/state/bookings/actions';
import { Color } from '../../../core/enums/Color';
import { BookingModalType } from '../../../core/state/bookings/types';
import { BookingLayout } from '../types';
import { useBookingLayout } from '../hooks';
import { getStateStatus } from '../../../core/state/bookings/selectors';
import { StateStatus } from '../../../core/enums/StateStatus';
import { useInternalUser } from '../../../core/hooks/useInternalUser';
import { isReadAllRole } from '../../../core/state/user/selectors';

import Button from '../../../components/form/button/Button';
import DateSelector from '../calendar/date-selector/DateSelector';
import LayoutSelector from './layout-selector/LayoutSelector';
import BookingSearch from './booking-search/BookingSearch';
import ToolbarPrintHeader from './toolbar-print-header/ToolbarPrintHeader';

import './Toolbar.scss';

interface Props {
  handlePrint: () => void;
}

const Toolbar: React.FC<Props> = ({ handlePrint }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const layout = useBookingLayout();
  const projectSettings = useSelector(selectProjectSettings);
  const stateStatus = useSelector(getStateStatus);
  const isInternal = useInternalUser();
  const isUserReader = useSelector(isReadAllRole);

  const openNewBookingModal = useCallback(
    () =>
      dispatch(
        BookingActions.openModal({
          type: isInternal ? BookingModalType.Internal : BookingModalType.LogisticPlan
        })
      ),
    [dispatch, isInternal]
  );

  const openFiltersModal = useCallback(() => dispatch(BookingActions.openModal({ type: BookingModalType.Filters })), [dispatch]);

  return (
    <div className="toolbar">
      <ToolbarPrintHeader />
      <div className="toolbar__left d-print-none">
        {layout === BookingLayout.Calendar && <DateSelector />}
        {layout === BookingLayout.List && <h3 className="toolbar__title">{t('booking.bookingList')}</h3>}
      </div>
      <div className="toolbar__middle">
        <BookingSearch />
        <Button onClick={openFiltersModal} buttonTypes={['outline', 'icon', 'border-icon']} color={Color.Grey} icon="filters">
          {t('booking.filters')}
        </Button>
      </div>
      <div className="toolbar__right">
        <Button onClick={() => handlePrint()} buttonTypes={['outline']} color={Color.Blue}>
          {t('booking.print')}
        </Button>
        {!isUserReader && (
          <Button onClick={openNewBookingModal} buttonTypes={['primary']} color={Color.Blue} disabled={!projectSettings}>
            + {t('booking.newDelivery')}
          </Button>
        )}
        <LayoutSelector />
      </div>
      {stateStatus === StateStatus.Busy && <div className="toolbar__loader" />}
    </div>
  );
};

export default Toolbar;
